<template>
  <header class="header" :class="{ 'scrolled': isScrolled }">
    <div class="container">
      <!-- Logo区域 -->
      <div class="logo-area">
        <img :src="logo" alt="PIZU GROUP" class="logo-img">
        <img :src="logoF" alt="PIZU GROUP" class="logoF-img">
      </div>

      <!-- 桌面端导航 -->
      <nav class="nav desktop-nav">
        <div
          v-for="item in menuData"
          :key="item.path"
          class="nav-item-wrapper"
          @mouseenter="handleNavHover(item)"
          @mouseleave="handleNavLeave(item)"
        >
          <!-- 一级菜单项 -->
          <a
            v-if="!item.children"
            :href="item.path"
            class="nav-item"
          >
            <span class="nav-text">{{ $t(item.i18nKey) }}</span>
            <span class="nav-indicator" :class="{ 'active': activeNavItem === item }"></span>
          </a>

          <!-- 有子菜单的一级菜单项 -->
          <div v-else class="nav-item-with-children">
            <span class="nav-text">
              {{ $t(item.i18nKey) }}
              <el-icon class="nav-arrow"><ArrowDown /></el-icon>
            </span>
            <span class="nav-indicator" :class="{ 'active': activeNavItem === item }"></span>

            <!-- 二级菜单下拉 -->
            <transition name="fade">
              <div
                v-if="showDropdown === item.path"
                class="dropdown-menu"
                @mouseenter="handleDropdownEnter"
                @mouseleave="handleDropdownLeave"
              >
                <a
                  v-for="child in item.children"
                  :key="child.path"
                  :href="child.path"
                  class="dropdown-item"
                  @mouseenter="handleDropdownHover(child)"
                >
                  <span class="dropdown-text">{{ $t(child.i18nKey) }}</span>
                  <span class="dropdown-indicator" :class="{ 'active': activeDropdownItem === child }"></span>
                </a>
              </div>
            </transition>
          </div>
        </div>
      </nav>

      <!-- 右侧功能区 -->
      <div class="header-actions">
        <!-- 语言切换 -->
        <div class="lang-switch">
          <el-dropdown @command="handleLangChange" trigger="click" placement="bottom-end">
            <span class="lang-current">
              <span class="lang-text">{{ $t(`lang.${currentLang}`) }}</span>
              <el-icon class="lang-icon"><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu class="lang-dropdown-menu">
                <el-dropdown-item
                  v-for="lang in languages"
                  :key="lang.value"
                  :command="lang.value"
                  :class="{ 'active': currentLang === lang.value }"
                >
                  {{ $t(`lang.${lang.value}`) }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>

        <!-- 移动端菜单按钮 -->
        <div class="mobile-menu-btn" @click="toggleMobileMenu">
          <div class="hamburger" :class="{ 'active': mobileMenuVisible }">
            <span class="line"></span>
            <span class="line"></span>
            <span class="line"></span>
          </div>
        </div>
      </div>
    </div>

    <!-- 移动端菜单 -->
    <transition name="slide-down">
      <div v-if="mobileMenuVisible" class="mobile-menu" @click.self="closeMobileMenu">
        <div class="mobile-menu-content">
          <div class="mobile-menu-header">
            <img :src="logo" alt="PIZU GROUP" class="mobile-logo">
            <div class="mobile-menu-close" @click="closeMobileMenu">
              <el-icon><Close /></el-icon>
            </div>
          </div>

          <div class="mobile-nav-items">
            <div
              v-for="item in menuData"
              :key="item.path"
              class="mobile-nav-item-wrapper"
            >
              <!-- 移动端一级菜单项（无二级菜单） -->
              <div
                v-if="!item.children"
                class="mobile-nav-item"
                @click="handleMobileNavClick(item)"
              >
                <span class="mobile-nav-text">{{ $t(item.i18nKey) }}</span>
              </div>
              <!-- 有子菜单的一级菜单项 -->
              <div
                v-else
                class="mobile-nav-item"
                :class="{ 'expanded': expandedMenu === item.path }"
                @click="toggleMobileSubmenu(item)"
              >
                <span class="mobile-nav-text">{{ $t(item.i18nKey) }}</span>
                <el-icon
                  class="mobile-nav-arrow"
                  :class="{ 'expanded': expandedMenu === item.path }"
                >
                  <ArrowRight />
                </el-icon>
              </div>
              <!-- 移动端二级菜单（替换原生a标签为div，避免页面刷新） -->
              <transition name="slide">
                <div
                  v-if="item.children && expandedMenu === item.path"
                  class="mobile-submenu"
                >
                  <div
                    v-for="child in item.children"
                    :key="child.path"
                    class="mobile-submenu-item"
                    @click="handleMobileNavClick(child)"
                  >
                    <span class="mobile-submenu-text">{{ $t(child.i18nKey) }}</span>
                    <el-icon class="mobile-submenu-icon"><ArrowRight /></el-icon>
                  </div>
                </div>
              </transition>
            </div>
          </div>

          <div class="mobile-menu-footer">
            <!-- 移动端语言切换 -->
            <div class="mobile-lang-switch">
              <div
                v-for="lang in languages"
                :key="lang.value"
                :class="['mobile-lang-item', { 'active': currentLang === lang.value }]"
                @click="switchMobileLang(lang.value)"
              >
                {{ $t(`lang.${lang.value}`) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </header>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { menuData } from '@/mock/data';
import { ArrowDown, ArrowRight, Close } from '@element-plus/icons-vue';
import logo from '@/assets/img/logo.png';
import logoF from '@/assets/img/logoF.png';
import { useRoute, useRouter } from 'vue-router';

// 获取i18n实例
const { locale } = useI18n();
const router = useRouter();
// 语言配置
const languages = [
  { value: 'cn', label: '简体中文' },
  { value: 'tc', label: '繁體中文（香港）' },
  { value: 'en', label: 'English' },
  // { value: 'ru', label: 'Русский' }
];

// ========== 新增：定义缓存键名（唯一标识，避免冲突） ==========
const LANG_STORAGE_KEY = 'pizu_current_lang';

// 状态管理
const currentLang = ref<string>('cn');
const mobileMenuVisible = ref(false);
const isScrolled = ref(false);
const activeNavItem = ref<any>(null);
const activeDropdownItem = ref<any>(null);
const showDropdown = ref<string | null>(null);
const expandedMenu = ref<string | null>(null);
let dropdownTimer: number | null = null;

// ========== 核心修改：从缓存初始化语言（优先级：localStorage > sessionStorage > I18n默认 > 简体中文） ==========
// 方式1：使用localStorage（持久化，推荐）
const savedLang = localStorage.getItem(LANG_STORAGE_KEY) || locale.value || 'cn';
// 方式2：使用sessionStorage（会话级，如需替换，注释上面一行，打开下面一行）
// const savedLang = sessionStorage.getItem(LANG_STORAGE_KEY) || locale.value || 'cn';

// 同步缓存语言到当前状态和I18n
currentLang.value = savedLang;
locale.value = savedLang;

// 滚动监听
const handleScroll = () => {
  isScrolled.value = window.scrollY > 50;
};

// 桌面端导航悬停效果
const handleNavHover = (item: any) => {
  activeNavItem.value = item;
  if (dropdownTimer) {
    clearTimeout(dropdownTimer);
    dropdownTimer = null;
  }
  if (item.children) {
    showDropdown.value = item.path;
  }
};

const handleNavLeave = (item: any) => {
  dropdownTimer = setTimeout(() => {
    if (activeNavItem.value === item) {
      activeNavItem.value = null;
    }
    if (showDropdown.value === item.path) {
      showDropdown.value = null;
      activeDropdownItem.value = null;
    }
  }, 150); // 延迟150ms关闭，让用户有时间移动到下拉菜单
};

// 二级菜单悬停效果
const handleDropdownHover = (child: any) => {
  activeDropdownItem.value = child;
};

// 鼠标进入下拉菜单区域
const handleDropdownEnter = () => {
  if (dropdownTimer) {
    clearTimeout(dropdownTimer);
    dropdownTimer = null;
  }
};

// 鼠标离开下拉菜单区域
const handleDropdownLeave = () => {
  dropdownTimer = setTimeout(() => {
    showDropdown.value = null;
    activeDropdownItem.value = null;
    activeNavItem.value = null;
  }, 150);
};

// ========== 核心修改：切换语言时存入缓存 ==========
const handleLangChange = (lang: string) => {
  currentLang.value = lang;
  locale.value = lang;
  // 方式1：存入localStorage（持久化，推荐）
  localStorage.setItem(LANG_STORAGE_KEY, lang);
  // 方式2：存入sessionStorage（会话级，如需替换，注释上面一行，打开下面一行）
  // sessionStorage.setItem(LANG_STORAGE_KEY, lang);
  console.log('切换到语言:', lang);
};

// 移动端语言切换（复用handleLangChange，自动存入缓存）
const switchMobileLang = (lang: string) => {
  handleLangChange(lang);
};

// 移动端菜单控制
const toggleMobileMenu = () => {
  mobileMenuVisible.value = !mobileMenuVisible.value;
  // 防止滚动穿透
  if (mobileMenuVisible.value) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
    expandedMenu.value = null;
  }
};

const closeMobileMenu = () => {
  mobileMenuVisible.value = false;
  document.body.style.overflow = '';
  expandedMenu.value = null;
};

// 移动端子菜单切换
const toggleMobileSubmenu = (item: any) => {
  if (item.children) {
    expandedMenu.value = expandedMenu.value === item.path ? null : item.path;
  }
};

// 移动端导航点击（核心：添加路由跳转逻辑）
const handleMobileNavClick = (item: any) => {
  console.log('导航到:', item.path)
  // 执行路由跳转（Vue Router的push方法，无页面刷新）
  router.push({ path: item.path })
  // 关闭移动端菜单（根据需求保留）
  closeMobileMenu()
}

// 生命周期
onMounted(() => {
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  document.body.style.overflow = '';
  if (dropdownTimer) {
    clearTimeout(dropdownTimer);
  }
});
</script>

<style scoped lang="scss">
// 引入比优集团VIS统一变量（与common.scss保持一致）
$primary-color: #A66C48;        // 主色1（C15M41Y69K0）
$primary-color-2: #4A3226;      // 主色2（深棕，C56M73Y95K27）
$secondary-color: #D64A00;      // 辅助色1（橙红，C40M60Y100K0）
$secondary-color-2: #C70000;    // 辅助色2（深红，C0M100Y100K10）
$white: #FFFFFF;                // 纯白（K0）
$gray-10: #F0EFEE;              // 极浅灰（C9M10Y9K0）
$gray-20: #CCCCCC;              // 浅灰（K20）
$gray-40: #999999;              // 中浅灰（K40）
$gray-60: #666666;              // 中灰（K60）
$gray-80: #333333;              // 深灰（K80）
$black: #000000;                // 纯黑（K100）
$shadow-light: rgba(0, 0, 0, 0.05);
$shadow-medium: rgba(0, 0, 0, 0.1);

/* Header 基础样式 */
.header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  padding: 20px 0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  background: #ffffff30;
  backdrop-filter: blur(0px);

  &.scrolled {
    background: rgba($white, 0.96);
    backdrop-filter: blur(10px);
    padding: 12px 0;
    box-shadow: 0 4px 20px $shadow-light;
    border-bottom: 1px solid rgba($primary-color, 0.1); // 主色衍生边框
  }

  .container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
    max-width: 1800px;
    margin: 0 auto;
    padding: 0 40px;
  }
}

/* Logo区域（符合VIS标志组合规范） */
.logo-area {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.logo-img {
  height: 40px;
  width: auto;
  object-fit: contain;
  transition: height 0.3s ease;
  margin-right: 5px;
  .scrolled & {
    height: 35px;
  }
}
.logoF-img{
  height: 30px !important;
}

/* 桌面端导航（字体符合VIS E01规范） */
.desktop-nav {
  display: flex;
  gap: 28px;
  flex: 1;
  justify-content: center;
  margin: 0 20px;
  font-family: 'Noto Sans HK', '思源黑体 CN', sans-serif; // VIS指定中文字体

  @media (max-width: 1200px) {
    gap: 20px;
  }
}

.nav-item-wrapper {
  position: relative;
  display: inline-block;
}

.nav-item,
.nav-item-with-children {
  position: relative;
  text-decoration: none;
  color: $primary-color-2; // 导航文字用主色2（深棕）
  font-weight: 500;
  font-size: 14px;
  padding: 8px 0;
  transition: all 0.3s ease;
  cursor: pointer;
  white-space: nowrap;
  min-width: max-content;
  display: block;

  &:hover {
    color: $secondary-color; // hover用辅助色1（橙红）

    .nav-indicator {
      width: 100%;
    }

    .nav-arrow {
      transform: rotate(180deg);
    }
  }
}

.nav-item-with-children {
  .nav-text {
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

.nav-arrow {
  font-size: 12px;
  transition: transform 0.3s ease;
  margin-left: 2px;
  color: $primary-color; // 箭头用主色
}

.nav-text {
  display: block;
  position: relative;
  z-index: 1;
  font-size: 0.9em;
  letter-spacing: 0.02em;
}

.nav-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background-color: $secondary-color; // 指示器用辅助色1
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &.active {
    width: 100%;
  }
}

/* 下拉菜单样式（符合VIS环境导视系统H系列风格） */
.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  min-width: 180px;
  background: $white;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  padding: 8px 0;
  margin-top: 10px;
  z-index: 1001;
  border: 1px solid rgba($primary-color, 0.1); // 边框用主色衍生

  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%);
    width: 12px;
    height: 12px;
    background: $white;
    border-left: 1px solid rgba($primary-color, 0.1);
    border-top: 1px solid rgba($primary-color, 0.1);
    transform: rotate(45deg);
  }
}

.dropdown-item {
  display: block;
  padding: 10px 20px;
  color: $primary-color-2; // 下拉项文字用主色2
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    color: $secondary-color; // hover用辅助色1
    background: rgba($primary-color, 0.05); // 背景用主色淡色

    .dropdown-indicator {
      width: 4px;
    }
  }
}

.dropdown-text {
  display: block;
  font-size: 13px;
  font-weight: 500;
}

.dropdown-indicator {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 16px;
  background-color: $secondary-color; // 指示器用辅助色1
  border-radius: 0 2px 2px 0;
  transition: width 0.3s ease;

  &.active {
    width: 4px;
  }
}

/* 下拉菜单动画 */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-10px);
}

/* 右侧功能区 */
.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

/* 语言切换（符合VIS行政办公系统G系列风格） */
.lang-switch {
  .lang-current {
    display: flex;
    align-items: center;
    gap: 6px;
    color: $primary-color-2; // 文字用主色2
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    padding: 6px 12px;
    border-radius: 20px;
    transition: all 0.3s ease;
    border: 1px solid rgba($primary-color, 0.2); // 边框用主色衍生
    background: rgba($white, 0.8);
    min-width: 70px;

    &:hover {
      background-color: rgba($primary-color, 0.05); // 背景用主色淡色
      border-color: $primary-color; // 边框用主色
    }
  }

  .lang-text {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 50px;
  }

  .lang-icon {
    font-size: 10px;
    transition: transform 0.3s ease;
    color: $primary-color; // 图标用主色
  }

  :deep(.el-dropdown) {
    &:hover .lang-icon {
      transform: rotate(180deg);
    }
  }

  /* 自定义下拉菜单样式 */
  :deep(.lang-dropdown-menu.el-dropdown-menu) {
    border: 1px solid rgba($primary-color, 0.1);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    min-width: 100px;

    .el-dropdown-menu__item {
      color: $primary-color-2; // 下拉项文字用主色2
      font-size: 13px;
      padding: 6px 16px;
      white-space: nowrap;

      &:hover {
        background-color: rgba($primary-color, 0.05); // 背景用主色淡色
        color: $secondary-color; // 文字用辅助色1
      }

      &.active {
        background-color: rgba($primary-color, 0.1); // 背景用主色淡色
        color: $secondary-color; // 文字用辅助色1
        font-weight: 500;
      }
    }
  }
}

/* 移动端菜单按钮 */
.mobile-menu-btn {
  display: none;
  cursor: pointer;
  padding: 8px;

  .hamburger {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 24px;
    height: 18px;
    transition: all 0.3s ease;

    .line {
      display: block;
      height: 2px;
      background-color: $primary-color-2; // 线条用主色2
      border-radius: 2px;
      transition: all 0.3s ease;
      transform-origin: center;
    }

    &.active {
      .line:nth-child(1) {
        transform: rotate(45deg) translate(6px, 6px);
      }

      .line:nth-child(2) {
        opacity: 0;
      }

      .line:nth-child(3) {
        transform: rotate(-45deg) translate(6px, -6px);
      }
    }
  }

  &:hover .hamburger .line {
    background-color: $secondary-color; // hover用辅助色1
  }
}

/* 移动端菜单（符合VIS环境导视系统H系列深色模式风格） */
.mobile-menu {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba($gray-80, 0.98); // 背景用深灰（K80）
  backdrop-filter: blur(10px);
  z-index: 999;
  overflow-y: auto;
  height: 110vh;
}

.mobile-menu-content {
  min-height: 100vh;
  padding: 80px 20px 40px;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
}

.mobile-menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba($white, 0.1);

  .mobile-logo {
    height: 35px;
    width: auto;
    object-fit: contain;
  }

  .mobile-menu-close {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba($white, 0.1);
    color: $white;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: rgba($primary-color, 0.2); // 背景用主色淡色
      transform: rotate(90deg);
    }
  }
}

.mobile-nav-items {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.mobile-nav-item-wrapper {
  border-bottom: 1px solid rgba($white, 0.1);
}

.mobile-nav-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px;
  text-decoration: none;
  color: $white;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover,
  &.expanded {
    background: rgba($primary-color, 0.1); // 背景用主色淡色
  }
}

.mobile-nav-text {
  flex: 1;
}

.mobile-nav-arrow {
  font-size: 14px;
  color: rgba($white, 0.6);
  transition: all 0.3s ease;

  &.expanded {
    transform: rotate(90deg);
    color: $secondary-color; // 展开用辅助色1
  }
}

/* 移动端二级菜单 */
.mobile-submenu {
  background: rgba($white, 0.03);
  border-left: 3px solid $secondary-color; // 边框用辅助色1
  margin-left: 20px;
  overflow: hidden;
}

.mobile-submenu-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  text-decoration: none;
  color: rgba($white, 0.9);
  font-size: 14px;
  font-weight: 400;
  border-bottom: 1px solid rgba($white, 0.05);
  transition: all 0.3s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba($primary-color, 0.1); // 背景用主色淡色
    color: $white;
    padding-left: 25px;

    .mobile-submenu-icon {
      transform: translateX(3px);
    }
  }
}

.mobile-submenu-text {
  flex: 1;
}

.mobile-submenu-icon {
  font-size: 12px;
  color: rgba($white, 0.5);
  transition: all 0.3s ease;

  &:hover {
    color: $secondary-color; // hover用辅助色1
  }
}

/* 子菜单动画 */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  max-height: 200px;
  overflow: hidden;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.mobile-menu-footer {
  margin-top: 40px;
  padding-top: 30px;
  border-top: 1px solid rgba($white, 0.1);
}

.mobile-lang-switch {
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
}

.mobile-lang-item {
  flex: 1;
  text-align: center;
  padding: 12px 0;
  border: 1px solid rgba($white, 0.3);
  border-radius: 25px;
  font-size: 13px;
  color: $white;
  cursor: pointer;
  transition: all 0.3s ease;

  &.active {
    background-color: $primary-color; // 背景用主色
    color: $white;
    border-color: $primary-color; // 边框用主色
    font-weight: 500;
  }

  &:hover:not(.active) {
    background-color: rgba($primary-color, 0.2); // 背景用主色淡色
    border-color: $primary-color; // 边框用主色
  }
}

/* 菜单动画 */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* 响应式样式 - 保持原有断点逻辑 */
@media (max-width: 1200px) {
  .header .container {
    padding: 0 30px;
  }

  .desktop-nav {
    gap: 16px;
  }

  .nav-text {
    font-size: 0.85em;
  }
}

/* 关键修复：1100px以下显示移动端菜单按钮，同时隐藏桌面导航 */
@media (max-width: 1100px) {
  .desktop-nav {
    display: none;
  }

  .header-actions {
    gap: 12px;
  }

  /* 1100px以下就显示移动端菜单按钮 */
  .mobile-menu-btn {
    display: block;
  }
}

@media (max-width: 992px) {
  .header .container {
    padding: 0 20px;
  }
}

@media (max-width: 768px) {
  .header {
    padding: 15px 0;

    &.scrolled {
      padding: 10px 0;
    }
  }

  .header .container {
    padding: 0 16px;
  }

  .logo-img {
    height: 35px;

    .scrolled & {
      height: 30px;
    }
  }

  .lang-switch {
    .lang-current {
      min-width: 60px;
      padding: 5px 10px;

      .lang-text {
        max-width: 40px;
      }
    }
  }
}

@media (max-width: 480px) {
  .header .container {
    padding: 0 12px;
  }

  .logo-img {
    height: 30px;

    .scrolled & {
      height: 25px;
    }
  }

  .mobile-menu-content {
    padding: 60px 16px 30px;
  }

  .mobile-nav-item,
  .mobile-submenu-item {
    padding: 16px;
    font-size: 14px;
  }

  .mobile-lang-switch {
    flex-direction: column;
    gap: 10px;
  }

  .lang-switch {
    .lang-current {
      padding: 4px 8px;
      font-size: 12px;

      .lang-text {
        max-width: 35px;
      }
    }
  }
}
</style>